import { Box, Button, IconButton, Typography, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { DeleteOutline } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useState, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { useGetJobTypes } from '@/raptorjob/application/adapters/useJobType';
import { JobSiteAutocompleteOption, useListJobSitesAutocomplete } from '@/jobs/application/adapters/listJobSitesAutocomplete';
import useAvailableVendors from '@/raptorjob/application/adapters/useAvailableVendors';
import { useGetJobFormOptions } from '@/jobs/application/adapters/getJobFormOptions';
import { JobSiteCreateModal } from '@/jobs/presentation/pages/JobsPage/JobDrawer/JobSiteCreateModal';
import tid from '@/common/application/utils/tid';
import { FormikDateTimePicker } from '@/common/presentation/components/forms/fields/FormikDateTimePicker';
import { useConfirmedByContacts } from '../../hook/useConfirmedByContacts';
import { useCreateJobOptionalFieldsPreferences } from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';
import type { NamedEntity } from '@/raptorjob/application/utils/jobDataExtractor';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';

type JobTypeData = NonNullable<ReturnType<typeof useGetJobTypes>['data']>['data'];
type JobTypeAutocompleteOption = NonNullable<NonNullable<JobTypeData>>[number];

interface JobSiteEntity {
  id: number;
  name: string;
  address?: string | null;
}

interface ERPJobInfoSectionProps {
  jobSiteSearchTerm: string;
  onJobSiteSearchTermChange: (value: string) => void;
  mode?: JobFormMode;
  vendor?: NamedEntity | null;
  jobType?: NamedEntity | null;
  jobSite?: JobSiteEntity | null;
}

export function ERPJobInfoSection({
  jobSiteSearchTerm,
  onJobSiteSearchTermChange,
  mode = JOB_FORM_MODES.CREATE,
  vendor,
  jobType,
  jobSite
}: ERPJobInfoSectionProps) {
  const { values, setFieldValue, initialValues } = useFormikContext<ERPJobFormValues>();
  const navigate = useNavigate();
  const { visibility: optionalFieldsVisibility, setFieldVisible, savedValues, updateSavedValues } = useCreateJobOptionalFieldsPreferences();

  const { data: jobTypes, isLoading: jobTypesLoading } = useGetJobTypes();
  const {
    options: jobSiteOptions,
    loading: jobSiteLoading,
    filterOptions: jobSiteFilterOptions,
    refetch: refetchJobSites
  } = useListJobSitesAutocomplete(jobSiteSearchTerm, 1000, values.customerId || undefined);

  const [selectedJobSite, setSelectedJobSite] = useState<JobSiteAutocompleteOption | null>(null);
  const [showVendorField, setShowVendorField] = useState(
    mode === JOB_FORM_MODES.CREATE ? optionalFieldsVisibility.showVendor || !!values.vendorId : !!values.vendorId
  );
  const [jobInformationSearchTerm, setJobInformationSearchTerm] = useState('');
  const [isCreateJobSiteModalOpen, setIsCreateJobSiteModalOpen] = useState(false);

  const { availableVendors, loading: vendorLoading } = useAvailableVendors();
  const { availableJobInformation, loading: jobInformationLoading, availableJobStatuses } = useGetJobFormOptions(false);

  useEffect(() => {
    if (values.vendorId && typeof values.vendorId !== 'string') {
      setFieldValue('vendorId', values.vendorId);
    }
  }, [values.vendorId]);

  const initialOption = useMemo(() => {
    if (values.vendorId && !vendorLoading && availableVendors.length > 0) {
      const foundVendor = availableVendors.find((v) => Number(v.id) === Number(values.vendorId));
      if (foundVendor) {
        return { value: foundVendor.id, label: foundVendor.name ?? 'Unknown Vendor' };
      }
    }

    // Fallback: use vendor data from jobData if available and ID matches
    if (vendor && vendor.id === values.vendorId && vendor.name) {
      return {
        value: vendor.id,
        label: vendor.name
      };
    }

    // Fallback for create mode: use persisted vendor name so the field displays before vendors list loads
    if (mode === JOB_FORM_MODES.CREATE && values.vendorId != null && savedValues.vendorName != null && savedValues.vendorName !== '') {
      return {
        value: values.vendorId,
        label: savedValues.vendorName
      };
    }

    return undefined;
  }, [vendorLoading, availableVendors, values.vendorId, vendor, mode, savedValues.vendorName]);

  // Create options directly from availableJobInformation with string values (names) instead of IDs
  const jobInformationOptionsWithStringValues = useMemo(() => {
    return availableJobInformation.map((jobInfo) => ({
      value: jobInfo.name ?? '',
      label: jobInfo.name ?? 'Unknown Job Information'
    }));
  }, [availableJobInformation]);

  // Filter options based on search term
  const filteredJobInformationOptions = useMemo(() => {
    if (!jobInformationSearchTerm) return jobInformationOptionsWithStringValues;
    const lowerSearchTerm = jobInformationSearchTerm.toLowerCase();
    return jobInformationOptionsWithStringValues.filter((option) => option.label.toLowerCase().includes(lowerSearchTerm));
  }, [jobInformationOptionsWithStringValues, jobInformationSearchTerm]);

  // Compute initial option for Job Information (Sequence) - updates when jobInformation loads
  const jobInformationInitialOption = useMemo<OptionType | undefined>(() => {
    if (!values.sequence || typeof values.sequence !== 'string') return undefined;

    const sequenceValue: string = values.sequence;

    // Find by sequence string value (name)
    if (availableJobInformation.length > 0) {
      const jobInfo = availableJobInformation.find((ji) => ji.name === sequenceValue);
      if (jobInfo && jobInfo.name && typeof jobInfo.name === 'string') {
        return {
          value: jobInfo.name,
          label: jobInfo.name
        };
      }
    }

    // If not found in available options, return the sequence value as-is (for edit mode)
    // sequenceValue is guaranteed to be a string here due to the check above
    return {
      value: sequenceValue,
      label: sequenceValue
    };
  }, [values.sequence, availableJobInformation]);

  // Compute initial option for Job Type - updates when jobTypes load
  // This ensures the field displays correctly even when options load asynchronously
  const jobTypeInitialOption = useMemo<OptionType | undefined>(() => {
    if (!values.jobTypeId) return undefined;

    // Try to find in loaded jobTypes
    if (jobTypes?.data) {
      const foundJobType = jobTypes.data.find((jt) => jt.id === values.jobTypeId);
      if (foundJobType) {
        return {
          value: foundJobType.id,
          label: foundJobType.name ?? 'Unknown Job Type'
        };
      }
    }

    // Fallback: use jobType data from jobData if available and ID matches
    if (jobType && jobType.id === values.jobTypeId && jobType.name) {
      return {
        value: jobType.id,
        label: jobType.name
      };
    }

    // Return undefined if not found - FormikAutocomplete will use currentOption logic
    return undefined;
  }, [values.jobTypeId, jobTypes?.data, jobType]);

  const jobSiteInitialOption = useMemo<JobSiteAutocompleteOption | undefined>(() => {
    if (!values.jobSiteId) return undefined;

    if (jobSiteOptions.length > 0) {
      const found = jobSiteOptions.find((opt) => opt.value === values.jobSiteId);
      if (found) return found as JobSiteAutocompleteOption;
    }

    if (jobSite && jobSite.id === values.jobSiteId && jobSite.name) {
      return {
        value: jobSite.id,
        label: jobSite.name,
        address: jobSite.address
      };
    }

    return undefined;
  }, [values.jobSiteId, jobSiteOptions, jobSite]);

  // Memoize job type options with fallback from jobData
  const jobTypeOptionsWithFallback = useMemo(() => {
    const baseOptions =
      jobTypes?.data?.map((jt: JobTypeAutocompleteOption) => ({
        value: jt.id,
        label: jt.name ?? 'Unknown Job Type'
      })) ?? [];

    // Add jobType from jobData if it's not already in the options list
    if (jobType && jobType.id && jobType.name) {
      const exists = baseOptions.some((opt) => Number(opt.value) === jobType.id);
      if (!exists) {
        baseOptions.push({
          value: jobType.id,
          label: jobType.name
        });
      }
    }

    return baseOptions;
  }, [jobTypes?.data, jobType]);

  // Memoize vendor options with fallback from jobData
  const vendorOptionsWithFallback = useMemo(() => {
    const baseOptions = availableVendors.map((v) => ({
      value: v.id,
      label: v.name ?? 'Unknown Vendor'
    }));

    // Add vendor from jobData if it's not already in the options list
    if (vendor && vendor.id && vendor.name) {
      const exists = baseOptions.some((opt) => Number(opt.value) === vendor.id);
      if (!exists) {
        baseOptions.push({
          value: vendor.id,
          label: vendor.name
        });
      }
    }

    return baseOptions;
  }, [availableVendors, vendor]);

  const jobSiteOptionsWithFallback = useMemo(() => {
    const baseOptions = [...(jobSiteOptions as JobSiteAutocompleteOption[])];

    if (jobSite && jobSite.id && jobSite.name) {
      const exists = baseOptions.some((opt) => Number(opt.value) === jobSite.id);
      if (!exists) {
        baseOptions.unshift({
          value: jobSite.id,
          label: jobSite.name,
          address: jobSite.address
        });
      }
    }

    return baseOptions;
  }, [jobSiteOptions, jobSite]);

  // Fix invalid jobTypeId values (e.g., if a string label was set instead of numeric ID)
  useEffect(() => {
    if (values.jobTypeId !== null && values.jobTypeId !== undefined) {
      // Check if jobTypeId is a string or NaN (invalid number)
      const jobTypeIdNum = typeof values.jobTypeId === 'number' ? values.jobTypeId : Number(values.jobTypeId);
      if (isNaN(jobTypeIdNum) && jobTypes?.data) {
        // Try to find the job type by name/label
        const jobTypeByName = jobTypes.data.find((jt) => jt.name === String(values.jobTypeId));
        if (jobTypeByName) {
          setFieldValue('jobTypeId', jobTypeByName.id);
        } else {
          // If not found, reset to null
          setFieldValue('jobTypeId', null);
        }
      } else if (typeof values.jobTypeId !== 'number' && !isNaN(jobTypeIdNum)) {
        // If it's a valid numeric string, convert to number
        setFieldValue('jobTypeId', jobTypeIdNum);
      }
    }
  }, [values.jobTypeId, jobTypes?.data, setFieldValue]);

  const { confirmedByContactOptions, confirmedByInitialOption, customerCallerLoading } = useConfirmedByContacts({
    customerId: values.customerId,
    confirmedBy: values.confirmedBy
  });

  const confirmedStatusOption = availableJobStatuses.find((status) => status.name?.toLowerCase().trim() === 'confirmed');
  const isJobConfirmed = confirmedStatusOption?.id === initialValues.statusId;
  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Job Information
      </Typography>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-around', gap: { xs: '0px', sm: '12px' }, flexDirection: { xs: 'column', sm: 'row' } }}
      >
        <FormikAutocomplete<JobSiteAutocompleteOption, ERPJobFormValues>
          key={`job-site-${values.jobSiteId}`}
          id="jobSiteId"
          label="Job Site"
          initialOption={jobSiteInitialOption}
          options={jobSiteOptionsWithFallback}
          optionsloading={jobSiteLoading}
          filterOptions={
            jobSiteFilterOptions as (options: JobSiteAutocompleteOption[], state: { inputValue: string }) => JobSiteAutocompleteOption[]
          }
          onInputChange={(_, inputValue, reason) => {
            if (reason !== 'selectOption' && reason !== 'reset') {
              onJobSiteSearchTermChange(inputValue);
            }
          }}
          getOptionLabel={(option) => {
            const address = (option as JobSiteAutocompleteOption).address ? ` - ${(option as JobSiteAutocompleteOption).address}` : '';
            return option.label ? `${option.label}${address}` : 'Unknown JobSite';
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          actions={
            mode === JOB_FORM_MODES.EDIT
              ? []
              : [
                  {
                    label: 'View All JobSites',
                    icon: <Add />,
                    onClick: () => navigate('/jobs?module=jobs&submodule=jobSites')
                  },
                  {
                    label: 'Add New JobSite',
                    icon: <Add />,
                    onClick: () => setIsCreateJobSiteModalOpen(true)
                  }
                ]
          }
          required
          disabled={!values.customerId}
          value={selectedJobSite}
          onChange={(value) => {
            if (Array.isArray(value)) {
              setSelectedJobSite((value[0] as JobSiteAutocompleteOption) || null);
            } else {
              setSelectedJobSite((value as JobSiteAutocompleteOption) || null);
            }
          }}
          sx={{ flex: 1 }}
          {...tid('jobs:create:job-site-autocomplete')}
        />
        <FormikAutocomplete<OptionType, ERPJobFormValues>
          key={`job-type-${values.jobTypeId}`}
          id="jobTypeId"
          label="Job Type"
          options={jobTypeOptionsWithFallback}
          optionsloading={jobTypesLoading}
          filterOptions={(options, state) => {
            const input = state.inputValue.trim().toLowerCase();
            if (!input) return options;
            return options.filter((o) => o.label.toLowerCase().includes(input));
          }}
          onInputChange={(_event, _inputValue, reason) => {
            // Prevent setting field value during typing - only allow selection or reset
            // This prevents validation errors when user is typing
            // The autocomplete will handle filtering internally via filterOptions

            if (reason === 'selectOption' || reason === 'reset') {
              // These are handled by onChange
              return;
            }
            // For other reasons (input, clear), don't update the field value
            // This prevents the field from being set to the typed string
          }}
          getOptionLabel={(option: OptionType) => option?.label ?? 'Unknown Job Type'}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option?.value === value?.value}
          initialOption={jobTypeInitialOption}
          onChange={(selectedOption) => {
            // Ensure we always set the numeric ID, never the label string
            if (selectedOption === null) {
              setFieldValue('jobTypeId', null);
            } else if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
              // Valid option object with numeric value
              setFieldValue('jobTypeId', selectedOption.value);
            } else if (typeof selectedOption === 'string' && jobTypes?.data) {
              // If a string was passed (e.g., user typed and clicked away), try to find by name
              const jobType = jobTypes.data.find((jt) => jt.name === selectedOption);
              if (jobType) {
                setFieldValue('jobTypeId', jobType.id);
              } else {
                // If not found, don't set invalid value - keep current or set to null
                // The useEffect will handle cleanup
              }
            }
          }}
          required
          sx={{ flex: 1 }}
          {...tid('jobs:create:job-type-autocomplete')}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          gap: '12px',
          flexDirection: { xs: 'column', sm: 'row' },
          width: '100%'
        }}
      >
        <FormikAutocomplete<OptionType, ERPJobFormValues>
          key={`sequence-${values.sequence}`}
          id="sequence"
          label="Sequence"
          options={filteredJobInformationOptions}
          optionsloading={jobInformationLoading}
          filterOptions={(options) => {
            // Sort options to put "Normal" at the top (case-insensitive)
            return [...options].sort((a, b) => {
              if (a.label.toLowerCase() === 'normal') return -1;
              if (b.label.toLowerCase() === 'normal') return 1;
              return 0;
            });
          }}
          onInputChange={(_event, value) => {
            setJobInformationSearchTerm(value);
          }}
          getOptionLabel={(option: OptionType) => option.label}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
          initialOption={jobInformationInitialOption}
          onChange={(selectedOption) => {
            if (selectedOption === null) {
              setFieldValue('sequence', null);
            } else if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
              // Set the string value (name) instead of ID
              setFieldValue('sequence', selectedOption.value);
            } else if (typeof selectedOption === 'string') {
              // If a string was passed directly, use it
              setFieldValue('sequence', selectedOption);
            }
          }}
          required
          sx={{ flex: 1 }}
          {...tid('jobs:create:sequence-select')}
        />
        {!showVendorField ? (
          <Button
            type="button"
            variant="text"
            onClick={() => {
              setShowVendorField(true);
              if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showVendor', true);
            }}
            startIcon={<AddCircleOutlineIcon />}
            sx={{ mb: 2, flex: 1, justifyContent: 'flex-start' }}
            {...tid('jobs:create:add-vendor-button')}
          >
            ADD VENDOR
          </Button>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <FormikAutocomplete<OptionType, ERPJobFormValues>
              id="vendorId"
              label="Vendor"
              options={vendorOptionsWithFallback}
              optionsloading={vendorLoading}
              initialOption={initialOption}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (!input) return options;
                return options.filter((o) => o.label.toLowerCase().includes(input));
              }}
              getOptionLabel={(option: OptionType) => option.label}
              isOptionEqualToValue={(option: OptionType, value: OptionType) => option.value === value.value}
              onChange={(selectedOption) => {
                if (mode === JOB_FORM_MODES.CREATE) {
                  if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption && 'label' in selectedOption) {
                    updateSavedValues({
                      vendorId: selectedOption.value as number,
                      vendorName: selectedOption.label ?? null
                    });
                  } else {
                    updateSavedValues({ vendorId: null, vendorName: null });
                  }
                }
              }}
              sx={{ flex: 1 }}
              {...tid('jobs:create:vendor-autocomplete')}
            />
            <IconButton
              title="Remove Vendor"
              onClick={() => {
                setShowVendorField(false);
                setFieldValue('vendorId', null);
                if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showVendor', false);
              }}
              sx={{ mb: '20px' }}
            >
              <DeleteOutline color="error" />
            </IconButton>
          </Box>
        )}
      </Box>

      {mode === JOB_FORM_MODES.EDIT && (
        <>
          <Box
            sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '12px',
              width: '100%'
            }}
          >
            <FormikAutocomplete<OptionType, ERPJobFormValues>
              key={`confirmed-by-${values.customerId}-${values.confirmedBy}`}
              id="confirmedBy"
              label="Confirmed By"
              options={confirmedByContactOptions}
              optionsloading={customerCallerLoading}
              filterOptions={(options, state) => {
                const input = state.inputValue.trim().toLowerCase();
                if (!input) return options;
                return options.filter((o) => o.label.toLowerCase().includes(input));
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              placeholder={customerCallerLoading ? 'Loading contacts...' : 'Select a contact'}
              disabled={!values.customerId || customerCallerLoading || confirmedByContactOptions.length === 0}
              initialOption={confirmedByInitialOption}
              onChange={(selectedOption) => {
                const isFirstTimeSettingConfirmedBy = !initialValues.confirmedBy;
                const hasSelectedOption = selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption;
                const shouldResetStatusToInitialValue = !hasSelectedOption && isFirstTimeSettingConfirmedBy && initialValues.statusId;

                if (!hasSelectedOption) {
                  setFieldValue('confirmedBy', null);
                }
                if (shouldResetStatusToInitialValue) {
                  setFieldValue('statusId', initialValues.statusId);
                }
                if (hasSelectedOption) {
                  setFieldValue('confirmedBy', selectedOption.value);

                  if (isFirstTimeSettingConfirmedBy && !values.confirmedAt) {
                    setFieldValue('confirmedAt', new Date());
                  }
                  if (isFirstTimeSettingConfirmedBy && confirmedStatusOption?.id && !isJobConfirmed) {
                    setFieldValue('statusId', confirmedStatusOption.id);
                  }
                }
              }}
              sx={{ width: '100%', minWidth: 0 }}
              {...tid('jobs:create:confirmed-by-autocomplete')}
            />
            <FormikDateTimePicker<ERPJobFormValues> id="confirmedAt" label="Confirmed Time" {...tid('jobs:create:confirmed-time-input')} />
          </Box>

          {!initialValues.confirmedBy && values.confirmedBy && !isJobConfirmed && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" {...tid('jobs:create:confirmed-by-alert')}>
                This will update the Job Status to Confirmed {!initialValues.confirmedAt ? 'and Confirmed Time' : ''}
              </Alert>
            </Box>
          )}
        </>
      )}

      <JobSiteCreateModal
        customerId={values.customerId ?? 0}
        customerName={values.customerName ?? ''}
        isOpen={isCreateJobSiteModalOpen && !!values.customerId}
        toggleModal={() => setIsCreateJobSiteModalOpen((state) => !state)}
        setJobSiteFieldValue={(jobSiteId) => setFieldValue('jobSiteId', jobSiteId)}
        refetchJobSites={refetchJobSites}
      />
    </>
  );
}
